/**
 * This is a simple deep clone using json conversion. It will deep clone properties, arrays and objects but NOT functions.
 * If function cloning is required use lodash/cloneDeep.
 */
export const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

export const isObject = (o: unknown): o is Record<string, unknown> =>
  o != null && typeof o === 'object' && (o as object).constructor.name === 'Object';

export const isDate = (o: unknown): o is Date => o != null && (o as object).constructor.name === 'Date';

export const isArray = (o: unknown): o is unknown[] => Array.isArray(o);

export const isEmpty = (val: unknown): boolean =>
  val === null || val === undefined || String(val).trim().length === 0;

export const isNumeric = (val: unknown): boolean => !Number.isNaN(Number(val));

export const isTrue = (val?: boolean): boolean => val !== undefined && val !== null && val === true;

export const isFalse = (val?: boolean): boolean => val !== undefined && val !== null && val === false;

export const isEmptyString = (val: unknown): val is string =>
  val !== undefined && val !== null && typeof val === 'string' && val.trim() === '';

/**
 * Reads a value from an API payload by key, ignoring property-name casing
 * (e.g. `id`/`Id`, `createdDate`/`CreatedDate`). Returns the first case-insensitive
 * match that is not `null`/`undefined`, mirroring a `?? ` fallback chain.
 */
export function readApiValue(source: Record<string, unknown>, key: string): unknown {
  const lowerKey = key.toLowerCase();

  for (const sourceKey of Object.keys(source)) {
    if (sourceKey.toLowerCase() === lowerKey && source[sourceKey] != null) {
      return source[sourceKey];
    }
  }

  return undefined;
}

/** Case-insensitive {@link readApiValue} coerced to a string, defaulting to `fallback`. */
export function readApiString(source: Record<string, unknown>, key: string, fallback = ''): string {
  const value = readApiValue(source, key);
  return value == null ? fallback : String(value);
}

/** Case-insensitive {@link readApiValue} coerced to a number, defaulting to `fallback`. */
export function readApiNumber(source: Record<string, unknown>, key: string, fallback = 0): number {
  const value = readApiValue(source, key);
  return value == null ? fallback : Number(value);
}

/** Case-insensitive {@link readApiValue} as an array, defaulting to `fallback`. */
export function readApiArray<T = unknown>(
  source: Record<string, unknown>,
  key: string,
  fallback: T[] = []
): T[] {
  const value = readApiValue(source, key);
  return value == null ? fallback : (value as T[]);
}

export function toDictionary(values: (string | number)[]): Record<string, boolean> {
  const dictionary: Record<string, boolean> = {};

  for (const value of values) {
    dictionary[value] = true;
  }

  return dictionary;
}

export const getObjectDiff = (
  current: unknown,
  previous: unknown
): Record<string, { previous: unknown; current: unknown }> => {
  const diff: Record<string, { previous: unknown; current: unknown }> = {};
  const cur = current != null && typeof current === 'object' && !Array.isArray(current) ? (current as Record<string, unknown>) : {};
  const prev = previous != null && typeof previous === 'object' && !Array.isArray(previous) ? (previous as Record<string, unknown>) : {};

  // Get all keys from both objects
  const allKeys = new Set([...Object.keys(cur), ...Object.keys(prev)]);

  allKeys.forEach(key => {
    const currentValue = cur[key];
    const previousValue = prev[key];
    
    if (JSON.stringify(currentValue) !== JSON.stringify(previousValue)) {
      diff[key] = {
        previous: previousValue,
        current: currentValue
      };
    }
  });
  
  return diff;
}

/**
 * Ensures specified object properties are arrays of strings
 * @param obj - The object to modify
 * @param keys - Array of property keys to ensure are arrays of strings
 * @example
 * const data = { ids: 'single', types: ['a', 1, true] };
 * ensureArrayOfStrings(data, ['ids', 'types']);
 * // Result: { ids: ['single'], types: ['a', '1', 'true'] }
 */
export const ensureArrayOfStrings = <T extends Record<string, unknown>>(obj: T, keys: (keyof T)[]): void => {
  keys.forEach((key) => {
    const value = obj[key];
    if (value === null || value === undefined) {
      obj[key] = [] as T[keyof T];
      return;
    }

    // If it's not already an array, wrap it in an array
    if (!Array.isArray(value)) {
      obj[key] = [value] as T[keyof T];
    }

    // Ensure all elements are strings
    obj[key] = (obj[key] as unknown[]).map((v) => String(v)) as T[keyof T];
  });
}
